'use client'
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { usePermissions } from '@/hooks/use-permissions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { Loader2, Plus, Pencil, Trash2, Save } from 'lucide-react'
import { formatCentsAsUsd } from '@/lib/billing/utils/format'
import { parseCreditsToMicroUsd, creditsInputValue } from '@/lib/billing/utils/credits'
import {
  BILLING_CHANNELS,
  DEFAULT_SHOPIFY_TOPUP_CYCLE_CAP_CENTS,
  DEFAULT_PARTNER_CREDIT_LIFETIME_MONTHS,
  DEFAULT_TOPUP_CREDIT_LIFETIME_MONTHS,
} from '@/lib/billing/constants'
import { useTranslations } from "next-intl"



// =============================================================================
// Types
// =============================================================================

interface BillingSettingsData {
  pool_trial_reserve_percent: number
  /** Global kill-switch for tenant-initiated plan change (Phase 3.3 route). */
  allow_self_serve_plan_change: boolean
  /** Ceiling on what one tenant may top up per Shopify billing cycle, in cents. */
  shopify_topup_cycle_cap_cents: number
  /** Months top-up credits last; null = never. */
  topup_credit_lifetime_months: number | null
  partner_credit_lifetime_months: number | null
}

interface TopupPackage {
  id: string
  name: string
  amount_microusd: number
  price_cents: number
  stripe_price_id: string | null
  billing_channel: string
  is_best_value: boolean
  is_active: boolean
  sort_order: number
}


// =============================================================================
// SettingsTab
// =============================================================================

export function SettingsTab() {
  const { formatCredits } = useCreditFormatters()


  const tUi = useTranslations("Billing.BillingSettingsTab")

  const { fetchWithAuth } = useAuth()
  const { can } = usePermissions()
  const canManage = can('billing.manage_plans')

  const [_settings, setSettings] = useState<BillingSettingsData | null>(null)
  const [packages, setPackages] = useState<TopupPackage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Settings form
  const [poolReserve, setPoolReserve] = useState(20)
  const [allowSelfServePlanChange, setAllowSelfServePlanChange] = useState(true)
  const [shopifyTopupCapCents, setShopifyTopupCapCents] = useState(DEFAULT_SHOPIFY_TOPUP_CYCLE_CAP_CENTS)
  // Empty = never.
  const [topupLifetimeMonths, setTopupLifetimeMonths] = useState(String(DEFAULT_TOPUP_CREDIT_LIFETIME_MONTHS))
  const [partnerLifetimeMonths, setPartnerLifetimeMonths] = useState(String(DEFAULT_PARTNER_CREDIT_LIFETIME_MONTHS))
  const [savingSettings, setSavingSettings] = useState(false)

  // Package dialog
  const [pkgDialogOpen, setPkgDialogOpen] = useState(false)
  const [editingPkg, setEditingPkg] = useState<TopupPackage | null>(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const [settingsRes, packagesRes] = await Promise.all([
        fetchWithAuth('/api/admin/billing/settings'),
        fetchWithAuth('/api/admin/billing/topup-packages'),
      ])

      if (!settingsRes.ok || !packagesRes.ok) {
        throw new Error('Failed to fetch settings data')
      }

      const settingsData = await settingsRes.json()
      const packagesData = await packagesRes.json()

      const s = settingsData.settings as BillingSettingsData | null
      setSettings(s)
      if (s) {
        setPoolReserve(s.pool_trial_reserve_percent)
        // Schema default is TRUE; treat missing/null as enabled for pre-migration rows.
        setAllowSelfServePlanChange(s.allow_self_serve_plan_change ?? true)
        setShopifyTopupCapCents(s.shopify_topup_cycle_cap_cents ?? DEFAULT_SHOPIFY_TOPUP_CYCLE_CAP_CENTS)
        setTopupLifetimeMonths(lifetimeInput(s.topup_credit_lifetime_months))
        setPartnerLifetimeMonths(lifetimeInput(s.partner_credit_lifetime_months))
      }
      setPackages(packagesData.packages ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToFetchData"))
    } finally {
      setLoading(false)
    }
  }, [fetchWithAuth, tUi])

  useEffect(() => { fetchData() }, [fetchData])

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true)
      const res = await fetchWithAuth('/api/admin/billing/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pool_trial_reserve_percent: poolReserve,
          allow_self_serve_plan_change: allowSelfServePlanChange,
          shopify_topup_cycle_cap_cents: shopifyTopupCapCents,
          topup_credit_lifetime_months: lifetimeValue(topupLifetimeMonths),
          partner_credit_lifetime_months: lifetimeValue(partnerLifetimeMonths),
        }),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to save settings')
      }
      fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSavingSettings(false)
    }
  }

  const handleDeletePkg = async (pkg: TopupPackage) => {
    if (!confirm(`Deactivate "${pkg.name}"?`)) return
    try {
      const res = await fetchWithAuth(`/api/admin/billing/topup-packages/${pkg.id}`, { method: 'DELETE' })
      if (!res.ok) {
        const data = await res.json()
        alert(data.error || 'Failed to deactivate')
        return
      }
      fetchData()
    } catch {
      alert('Failed to deactivate package')
    }
  }

  const handleSavePkg = async (data: Record<string, unknown>) => {
    const url = editingPkg
      ? `/api/admin/billing/topup-packages/${editingPkg.id}`
      : '/api/admin/billing/topup-packages'
    const method = editingPkg ? 'PATCH' : 'POST'

    const res = await fetchWithAuth(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!res.ok) {
      const err = await res.json()
      throw new Error(err.error || 'Failed to save package')
    }

    setPkgDialogOpen(false)
    fetchData()
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return <div className="text-center text-destructive py-12">{error}</div>
  }

  return (
    <div className="space-y-6">
      {/* Global Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base"><span key={tUi("globalSettings")}>{tUi("globalSettings")}</span></CardTitle>
          <CardDescription>
            <span key={tUi("poolReserveAndCreditLifetimesTheDefaultLow")}>{tUi("poolReserveAndCreditLifetimesTheDefaultLow")}</span></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label><span key={tUi("poolTrialReserve")}>{tUi("poolTrialReserve")}</span></Label>
              <Input
                type="number" min={0} max={100}
                value={poolReserve}
                onChange={(e) => setPoolReserve(parseInt(e.target.value) || 0)}
                disabled={!canManage}
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="topup-credit-lifetime"><span key={tUi("topUpCreditsExpireAfterMonths")}>{tUi("topUpCreditsExpireAfterMonths")}</span></Label>
              <Input
                id="topup-credit-lifetime"
                type="number" min={1} max={120} step={1}
                placeholder={tUi("never")}
                value={topupLifetimeMonths}
                onChange={(e) => setTopupLifetimeMonths(e.target.value)}
                disabled={!canManage}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                <span key={tUi("countedFromEachTopUpOnBothChannels")}>{tUi("countedFromEachTopUpOnBothChannels")}</span></p>
            </div>
            <div>
              <Label htmlFor="partner-credit-lifetime"><span key={tUi("partnerCreditsExpireAfterMonths")}>{tUi("partnerCreditsExpireAfterMonths")}</span></Label>
              <Input
                id="partner-credit-lifetime"
                type="number" min={1} max={120} step={1}
                placeholder={tUi("never")}
                value={partnerLifetimeMonths}
                onChange={(e) => setPartnerLifetimeMonths(e.target.value)}
                disabled={!canManage}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                <span key={tUi("countedFromEachTransferOrClientBonusCredits")}>{tUi("countedFromEachTransferOrClientBonusCredits")}</span></p>
            </div>
          </div>
          <div className="flex items-start justify-between rounded-md border p-3 gap-4">
            <div className="space-y-0.5 min-w-0">
              <Label htmlFor="self-serve-plan-change"><span key={tUi("allowTenantsToChangePlan")}>{tUi("allowTenantsToChangePlan")}</span></Label>
              <p className="text-xs text-muted-foreground">
                <span><span key={tUi("whenOffTheTenantBillingPageHidesThe")}>{tUi("whenOffTheTenantBillingPageHidesThe")}</span></span>
                <code className="mx-1">POST /api/billing/subscription/change-plan</code>
                <span><span key={tUi("returns403")}>{tUi("returns403")}</span></span> <code>plan_change_disabled</code><span><span key={tUi("perTenantOverridesForceAllowForceDenyOn")}>{tUi("perTenantOverridesForceAllowForceDenyOn")}</span></span>
              </p>
            </div>
            <Switch
              id="self-serve-plan-change"
              checked={allowSelfServePlanChange}
              onCheckedChange={setAllowSelfServePlanChange}
              disabled={!canManage}
            />
          </div>
          {canManage && (
            <Button onClick={handleSaveSettings} disabled={savingSettings} size="sm">
              {savingSettings ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              <span><span key={tUi("saveSettings")}>{tUi("saveSettings")}</span></span>
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Stripe top-up packages */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-base"><span key={tUi("topUpPackagesStripe")}>{tUi("topUpPackagesStripe")}</span></CardTitle>
              <CardDescription>
                <span key={tUi("theAmountsStripeBilledTenantsCanBuyShopify")}>{tUi("theAmountsStripeBilledTenantsCanBuyShopify")}</span></CardDescription>
            </div>
            {canManage && (
              <Button size="sm" onClick={() => { setEditingPkg(null); setPkgDialogOpen(true) }}>
                <Plus className="h-4 w-4 mr-1" /> <span><span key={tUi("addPackage")}>{tUi("addPackage")}</span></span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead><span key={tUi("name")}>{tUi("name")}</span></TableHead>
                <TableHead><span key={tUi("amount")}>{tUi("amount")}</span></TableHead>
                <TableHead><span key={tUi("price")}>{tUi("price")}</span></TableHead>
                <TableHead><span key={tUi("channel")}>{tUi("channel")}</span></TableHead>
                <TableHead><span key={tUi("status")}>{tUi("status")}</span></TableHead>
                {canManage && <TableHead className="w-[100px]"><span key={tUi("actions")}>{tUi("actions")}</span></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg.id} className={!pkg.is_active ? 'opacity-50' : ''}>
                  <TableCell className="font-medium">
                    {pkg.name}
                    {pkg.is_best_value && <Badge variant="default" className="ml-2"><span key={tUi("bestValue")}>{tUi("bestValue")}</span></Badge>}
                  </TableCell>
                  <TableCell>{formatCredits(pkg.amount_microusd)}</TableCell>
                  <TableCell><span>$</span>{(pkg.price_cents / 100).toFixed(2)}</TableCell>
                  <TableCell><Badge variant="outline">{pkg.billing_channel}</Badge></TableCell>
                  <TableCell>
                    {pkg.is_active
                      ? <Badge variant="default"><span key={tUi("active")}>{tUi("active")}</span></Badge>
                      : <Badge variant="outline"><span key={tUi("inactive")}>{tUi("inactive")}</span></Badge>}
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setEditingPkg(pkg); setPkgDialogOpen(true) }}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {pkg.is_active && (
                          <Button variant="ghost" size="icon" onClick={() => handleDeletePkg(pkg)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {packages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canManage ? 6 : 5} className="text-center text-muted-foreground py-8">
                    <span key={tUi("noTopupPackagesConfigured")}>{tUi("noTopupPackagesConfigured")}</span></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Shopify top-ups — read-only explanation plus the one thing we do control */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base"><span key={tUi("shopifyTopUps")}>{tUi("shopifyTopUps")}</span></CardTitle>
          <CardDescription>
            <span key={tUi("readFromEachMerchantSOwnAppPricing")}>{tUi("readFromEachMerchantSOwnAppPricing")}</span></CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            <li>
              <span><span key={tUi("amountsAreThe")}>{tUi("amountsAreThe")}</span></span> <code>topup-</code> <span><span key={tUi("usageMetersOnThePlanAuthoredInThe")}>{tUi("usageMetersOnThePlanAuthoredInThe")}</span></span>
            </li>
            <li><span key={tUi("shopifyOwnsThePriceTheMerchantIsBilled")}>{tUi("shopifyOwnsThePriceTheMerchantIsBilled")}</span></li>
            <li>
              <span key={tUi("creditIsThePrice11PlusThe")}>{tUi("creditIsThePrice11PlusThe")}</span></li>
          </ul>
          <div className="max-w-xs">
            <Label><span key={tUi("exposureCapPerBillingCycle")}>{tUi("exposureCapPerBillingCycle")}</span></Label>
            <Input
              type="number" min={0} step="1"
              value={(shopifyTopupCapCents / 100).toFixed(2)}
              onChange={(e) =>
                setShopifyTopupCapCents(Math.round((parseFloat(e.target.value) || 0) * 100))
              }
              disabled={!canManage}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              <span><span key={tUi("shopifyUsageIsPostpaidAndOffersNoCap")}>{tUi("shopifyUsageIsPostpaidAndOffersNoCap")}</span></span>{' '}
              {formatCentsAsUsd(DEFAULT_SHOPIFY_TOPUP_CYCLE_CAP_CENTS)}<span><span key={tUi("aSingleTenantCanBeGivenADifferent")}>{tUi("aSingleTenantCanBeGivenADifferent")}</span></span>
            </p>
          </div>
          {canManage && (
            <Button onClick={handleSaveSettings} disabled={savingSettings} size="sm">
              {savingSettings ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              <span><span key={tUi("saveSettings")}>{tUi("saveSettings")}</span></span>
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Package Dialog */}
      <TopupPackageDialog
        open={pkgDialogOpen}
        onOpenChange={setPkgDialogOpen}
        pkg={editingPkg}
        onSave={handleSavePkg}
      />
    </div>
  )
}

// =============================================================================
// TopupPackageDialog
// =============================================================================

function TopupPackageDialog({
  open, onOpenChange, pkg, onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pkg: TopupPackage | null
  onSave: (data: Record<string, unknown>) => Promise<void>
}) {

  const tUi = useTranslations("Billing.BillingSettingsTab")
  const tCommonUi = useTranslations("Common")

  const isEdit = !!pkg
  const [name, setName] = useState('')
  const [amountCredits, setAmountCredits] = useState('')
  const [priceUsd, setPriceUsd] = useState('')
  const [isBestValue, setIsBestValue] = useState(false)
  const [isActive, setIsActive] = useState(true)
  const [sortOrder, setSortOrder] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      if (pkg) {
        setName(pkg.name)
        setAmountCredits(creditsInputValue(pkg.amount_microusd))
        setPriceUsd((pkg.price_cents / 100).toString())
        setIsBestValue(pkg.is_best_value)
        setIsActive(pkg.is_active)
        setSortOrder(pkg.sort_order)
      } else {
        setName('')
        setAmountCredits('')
        setPriceUsd('')
        setIsBestValue(false)
        setIsActive(true)
        setSortOrder(0)
      }
      setError(null)
    }
  }, [open, pkg])

  const handleSubmit = async () => {
    if (!name.trim()) { setError(tUi("nameIsRequired")); return }
    const amountMicro = parseCreditsToMicroUsd(amountCredits)
    const priceCents = Math.round(parseFloat(priceUsd) * 100)
    if (amountMicro === null || amountMicro <= 0) { setError(tUi("amountMustBePositive")); return }
    if (isNaN(priceCents) || priceCents <= 0) { setError(tUi("priceMustBePositive")); return }

    try {
      setSaving(true)
      setError(null)
      await onSave({
        name: name.trim(),
        amount_microusd: amountMicro,
        price_cents: priceCents,
        // Only on create: a legacy Shopify row keeps its channel instead of being
        // silently converted, and the route refuses to mint a new one either way.
        ...(isEdit ? {} : { billing_channel: BILLING_CHANNELS.STRIPE }),
        is_best_value: isBestValue,
        is_active: isActive,
        sort_order: sortOrder,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToSave"))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle><span key={isEdit ? tUi("editPackage") : tUi("addPackage")}>{isEdit ? tUi("editPackage") : tUi("addPackage")}</span></DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label><span key={tUi("name")}>{tUi("name")}</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={tUi("label2500Credits")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label><span key={tUi("balanceAmountCredits")}>{tUi("balanceAmountCredits")}</span></Label>
              <Input value={amountCredits} onChange={(e) => setAmountCredits(e.target.value)} placeholder="2500" />
            </div>
            <div>
              <Label><span key={tUi("price2")}>{tUi("price2")}</span></Label>
              <Input value={priceUsd} onChange={(e) => setPriceUsd(e.target.value)} placeholder="25" />
            </div>
          </div>
          <div>
            <Label><span key={tUi("sortOrder")}>{tUi("sortOrder")}</span></Label>
            <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)} />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch checked={isBestValue} onCheckedChange={setIsBestValue} />
              <Label><span key={tUi("bestValue")}>{tUi("bestValue")}</span></Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={isActive} onCheckedChange={setIsActive} />
              <Label><span key={tUi("active")}>{tUi("active")}</span></Label>
            </div>
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            <span key={isEdit ? tUi("save") : tUi("create")}>{isEdit ? tUi("save") : tUi("create")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Empty input = never. */
function lifetimeInput(months: number | null | undefined): string {
  return months == null ? '' : String(months)
}

function lifetimeValue(input: string): number | null {
  return input.trim() === '' ? null : Number(input)
}
